// @view/panels/authentication/CreateAccount.panel.tsx
import EmptyView from "@view/EmptyView";
import React from "react";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import * as styles from "@styles/CreateAccount.css";
import "@styles/util/CreateAccount.global.css";
import type { EmitOnAction } from "@ps-bridge/capabilitiesTypes";
import type { Ok } from "@ps-bridge/ok";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { OrganizationSignupRequest, PlanTier } from "@ps-bridge/models/clients/organizationSignup";
import { onInputAction } from "@view/effects/inputHandlers";
import * as Option from "@utils/Option";

export type CreateAccountSignupPhase = "complete" | "form";
export type CreateAccountModePhase = "choose" | "org";
export type CreateAccountStepPhase = "account" | "organization" | "plan" | "billing";
export type CreateAccountPasswordBadgePhase = "hidden" | "match" | "mismatch";
export type CreateAccountFormErrorPhase = "hidden" | "visible";
export type CreateAccountBackPhase = "first" | "back";
export type CreateAccountPrimaryPhase = "continue" | "submit";

export type CreateAccountPlanOption = {
  id: PlanTier;
  name: string;
  price: string;
  detail: string;
  highlight: string;
};

const goalOptions = [
  "Automate audit-ready exports",
  "Streamline recurring client packets",
  "Consolidate document workflows",
  "Improve compliance reporting",
  "Replace manual PDF assembly",
];

export type CreateAccountPanelViewProps = {
  mode: "choose" | "org";
  stepIndex: number;
  stepNumber: number;
  stepCount: number;
  stepLabel: string;
  stepper: Array<{
    label: string;
    number: number;
    isActive: boolean;
    isComplete: boolean;
  }>;
  formError: OptionTs<string>;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
  goals: string[];
  planTier: OptionTs<PlanTier>;
  billingName: string;
  billingCard: string;
  billingExp: string;
  billingCvc: string;
  billingNotice: string;
  billingView: React.ReactElement;
  billingStatus: string;
  consentView: React.ReactElement;
  signupComplete: boolean;
  onModeChange: EmitOnAction<"choose" | "org">;
  onStepNext: EmitOnAction<Ok>;
  onStepBack: EmitOnAction<Ok>;
  onStepReset: EmitOnAction<Ok>;
  onFormErrorChange: EmitOnAction<OptionTs<string>>;
  onEmailChange: EmitOnAction<string>;
  onFirstNameChange: EmitOnAction<string>;
  onLastNameChange: EmitOnAction<string>;
  onPasswordChange: EmitOnAction<string>;
  onConfirmPasswordChange: EmitOnAction<string>;
  onOrganizationNameChange: EmitOnAction<string>;
  onGoalsChange: EmitOnAction<string[]>;
  onPlanTierChange: EmitOnAction<OptionTs<PlanTier>>;
  onBillingNameChange: EmitOnAction<string>;
  onBillingCardChange: EmitOnAction<string>;
  onBillingExpChange: EmitOnAction<string>;
  onBillingCvcChange: EmitOnAction<string>;
  onOrganizationSignup: EmitOnAction<OptionTs<OrganizationSignupRequest>>;
  onProceedToApp: EmitOnAction<{ email: string; password: string }>;
  onNavigateToLogin: EmitOnAction<Ok>;
};

export type CreateAccountPanelProps = CreateAccountPanelViewProps & {
  signupPhase: CreateAccountSignupPhase;
  modePhase: CreateAccountModePhase;
  stepPhase: CreateAccountStepPhase;
  passwordBadgePhase: CreateAccountPasswordBadgePhase;
  formErrorPhase: CreateAccountFormErrorPhase;
  backPhase: CreateAccountBackPhase;
  primaryPhase: CreateAccountPrimaryPhase;
  isContinueDisabled: boolean;
  isSubmitDisabled: boolean;
  formErrorMessage: string;
  okValue: Ok;
  runIO: <A>(value: IOts<A>) => A;
  planOptions: Array<CreateAccountPlanOption>;
  onToggleGoal: (goal: string) => IOts<Ok>;
  onResetToChoice: IOts<Ok>;
  onHandleNext: IOts<Ok>;
  onHandleBack: IOts<Ok>;
  onHandleSubmit: IOts<Ok>;
};

const CreateAccountPanel: React.FC<CreateAccountPanelProps> = ({
  stepNumber,
  stepCount,
  stepLabel,
  stepper,
  email,
  firstName,
  lastName,
  password,
  confirmPassword,
  organizationName,
  goals,
  planTier,
  billingName: _billingName,
  billingCard: _billingCard,
  billingExp: _billingExp,
  billingCvc: _billingCvc,
  billingNotice,
  billingView,
  billingStatus,
  consentView,
  onModeChange,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onOrganizationNameChange,
  onGoalsChange: _onGoalsChange,
  onPlanTierChange,
  onBillingNameChange: _onBillingNameChange,
  onBillingCardChange: _onBillingCardChange,
  onBillingExpChange: _onBillingExpChange,
  onBillingCvcChange: _onBillingCvcChange,
  onOrganizationSignup: _onOrganizationSignup,
  onProceedToApp,
  onNavigateToLogin,
  signupPhase,
  modePhase,
  stepPhase,
  passwordBadgePhase,
  formErrorPhase,
  backPhase,
  primaryPhase,
  isContinueDisabled,
  isSubmitDisabled,
  formErrorMessage,
  okValue,
  runIO,
  planOptions,
  onToggleGoal,
  onResetToChoice,
  onHandleNext,
  onHandleBack,
  onHandleSubmit,
}) => {
  const onInput = (handler: (value: string) => IOts<Ok>) =>
    onInputAction(handler);

  const passwordBadgeView = {
    hidden: <EmptyView />,
    match: <div className={styles.matchBadge}>Passwords match</div>,
    mismatch: (
      <div className={styles.matchBadgeError}>Passwords do not match</div>
    ),
  } as const satisfies Record<CreateAccountPasswordBadgePhase, React.ReactElement>;

  const formErrorView = {
    hidden: <EmptyView />,
    visible: <div className={styles.errorBanner}>{formErrorMessage}</div>,
  } as const satisfies Record<CreateAccountFormErrorPhase, React.ReactElement>;

  const backButtonView = {
    first: (
      <Button
        variant="ghost"
        className={styles.textButton}
        onClick={() => runIO(onResetToChoice)}
        asChild={Option.none()}
      >
        Back to options
      </Button>
    ),
    back: (
      <Button
        variant="ghost"
        className={styles.textButton}
        onClick={() => runIO(onHandleBack)}
        asChild={Option.none()}
      >
        Back
      </Button>
    ),
  } as const satisfies Record<CreateAccountBackPhase, React.ReactElement>;

  const primaryActionView = {
    continue: (
      <Button
        className={styles.primaryButton}
        onClick={() => runIO(onHandleNext)}
        disabled={isContinueDisabled}
        asChild={Option.none()}
      >
        Continue
      </Button>
    ),
    submit: (
      <Button
        className={styles.primaryButton}
        onClick={() => runIO(onHandleSubmit)}
        disabled={isSubmitDisabled}
        asChild={Option.none()}
      >
        Create organization
      </Button>
    ),
  } as const satisfies Record<CreateAccountPrimaryPhase, React.ReactElement>;

  const stepBodyView = {
    account: (
      <div className={styles.formGrid}>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>First name</label>
            <Input
              type="text"
              placeholder="Jane"
              value={firstName}
              onChange={onInput(onFirstNameChange)}
              autoComplete="given-name"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Last name</label>
            <Input
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={onInput(onLastNameChange)}
              autoComplete="family-name"
            />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Work email</label>
          <Input
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={onInput(onEmailChange)}
            autoComplete="email"
          />
        </div>
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>Password</label>
            <Input
              type="password"
              placeholder="Create a strong password"
              value={password}
              onChange={onInput(onPasswordChange)}
              autoComplete="new-password"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Confirm</label>
            <Input
              type="password"
              placeholder="Repeat password"
              value={confirmPassword}
              onChange={onInput(onConfirmPasswordChange)}
              autoComplete="new-password"
            />
          </div>
        </div>
        {passwordBadgeView[passwordBadgePhase]}
      </div>
    ),
    organization: (
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label}>Organization name</label>
          <Input
            type="text"
            placeholder="Aurelia Advisory Group"
            value={organizationName}
            onChange={onInput(onOrganizationNameChange)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>What are your goals? (optional)</label>
          <div className={styles.goalGrid}>
            {goalOptions.map((goal) => (
              <label key={goal} className={styles.goalOption}>
                <input
                  type="checkbox"
                  checked={goals.includes(goal)}
                  onChange={() => runIO(onToggleGoal(goal))}
                />
                <span>{goal}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    ),
    plan: (
      <div className={styles.planGrid}>
        {planOptions.map((plan) => (
          <button
            key={plan.id}
            type="button"
            className={`${styles.planCard} ${
              Option.fold(planTier, () => false, (value) => value === plan.id)
                ? styles.planCardSelected
                : ""
            }`}
            onClick={() => runIO(onPlanTierChange(Option.some(plan.id)))}
          >
            <div className={styles.planHeader}>
              <div>
                <div className={styles.planName}>{plan.name}</div>
                <div className={styles.planPrice}>{plan.price}</div>
              </div>
              <div className={styles.planTag}>{plan.highlight}</div>
            </div>
            <div className={styles.planDetail}>{plan.detail}</div>
          </button>
        ))}
      </div>
    ),
    billing: (
      <div className={styles.formGrid}>
        <div className={styles.noticeBox}>
          {billingNotice}
        </div>
        {billingView}
        <div className={styles.reviewBox}>
          <div>
            <div className={styles.reviewLabel}>Organization</div>
            <div className={styles.reviewValue}>{organizationName || "-"}</div>
          </div>
          <div>
            <div className={styles.reviewLabel}>Plan</div>
            <div className={styles.reviewValue}>
              {Option.fold(planTier, () => "-", (value: PlanTier) =>
                planOptions.find((plan) => plan.id === value)?.name ?? "-",
              )}
            </div>
          </div>
          <div>
            <div className={styles.reviewLabel}>Goals</div>
            <div className={styles.reviewValue}>
              {goals.length ? goals.join(", ") : "Not specified"}
            </div>
          </div>
          <div>
            <div className={styles.reviewLabel}>Billing</div>
            <div className={styles.reviewValue}>{billingStatus}</div>
          </div>
        </div>
      </div>
    ),
  } as const satisfies Record<CreateAccountStepPhase, React.ReactElement>;

  const leftPanelView = {
    choose: (
      <ul className={styles.featureList}>
        <li>Invite teammates with seat-aware access.</li>
        <li>Track plan usage and billing history.</li>
        <li>Launch with a guided setup in minutes.</li>
      </ul>
    ),
    org: (
      <div className={styles.stepper}>
        {stepper.map((stepItem) => (
          <div
            key={stepItem.label}
            className={`${styles.step} ${stepItem.isActive ? styles.stepActive : ""} ${
              stepItem.isComplete ? styles.stepComplete : ""
            }`}
          >
            <div className={styles.stepIndex}>{stepItem.number}</div>
            <div className={styles.stepLabel}>{stepItem.label}</div>
          </div>
        ))}
      </div>
    ),
  } as const satisfies Record<CreateAccountModePhase, React.ReactElement>;

  const rightPanelView = {
    choose: (
      <Card className={styles.card}>
        <CardContent>
          <CardTitle className={styles.cardTitle}>Get started</CardTitle>
          <p className={styles.cardSubtitle}>Choose how you want to join Ouroboros.</p>
          <div className={styles.choiceGrid}>
            <div className={styles.choiceCardAccent}>
              <div>
                <h3>Create a new organization</h3>
                <p>Set up your plan, invite teammates, and become the first super admin.</p>
              </div>
              <Button
                className={styles.choiceButton}
                onClick={() => runIO(onModeChange("org"))}
                data-testid="create-account-start-setup"
                asChild={Option.none()}
              >
                Start setup
              </Button>
            </div>
            <div className={styles.choiceCard}>
              <div>
                <h3>Join an existing organization</h3>
                <p>SSO-based access for teams that already have a workspace.</p>
              </div>
              <Button className={styles.choiceButton} disabled asChild={Option.none()}>
                SSO (coming soon)
              </Button>
            </div>
          </div>
          <div className={styles.loginRow}>
            <span>Already have an account?</span>
            <Button
              variant="ghost"
              className={styles.textButton}
              onClick={() => runIO(onNavigateToLogin(okValue))}
              data-testid="create-account-login"
              asChild={Option.none()}
            >
              Login
            </Button>
          </div>
        </CardContent>
      </Card>
    ),
    org: (
      <Card className={styles.card}>
        <CardContent>
          <div className={styles.formHeader}>
            <span className={styles.kicker}>
              Step {stepNumber} of {stepCount}
            </span>
            <CardTitle className={styles.cardTitle}>{stepLabel}</CardTitle>
            <p className={styles.cardSubtitle}>
              Let us know a bit more so we can tailor the workspace.
            </p>
          </div>
          {formErrorView[formErrorPhase]}
          {stepBodyView[stepPhase]}
          {consentView}
          <div className={styles.formActions}>
            {backButtonView[backPhase]}
            {primaryActionView[primaryPhase]}
          </div>
        </CardContent>
      </Card>
    ),
  } as const satisfies Record<CreateAccountModePhase, React.ReactElement>;

  const signupView = {
    complete: (
      <div className={styles.page} data-testid="page-createAccount" data-page-state="ready">
        <div className={styles.shell}>
          <aside className={styles.leftPanel}>
            <span className={styles.brandBadge}>Ouroboros AI</span>
            <h1 className={styles.headline}>You are almost there.</h1>
            <p className={styles.subheadline}>
              Check your inbox to validate your email. Once confirmed, you will have full access.
            </p>
            <ul className={styles.featureList}>
              <li>Invite teammates while you wait.</li>
              <li>Your account stays in pending validation.</li>
              <li>We will unlock the full workspace after verification.</li>
            </ul>
          </aside>
          <section className={styles.rightPanel}>
            <Card className={styles.card}>
              <CardContent>
                <CardTitle className={styles.cardTitle}>Signup complete</CardTitle>
                <p className={styles.cardSubtitle}>
                  We just sent a verification link to your email. Please confirm to activate the workspace.
                </p>
                <div className={styles.confirmationBox}>
                  <div>
                    <div className={styles.confirmationTitle}>Pending email validation</div>
                    <div className={styles.confirmationText}>
                      You can still invite users while validation is pending.
                    </div>
                  </div>
                  <Button
                    className={styles.primaryButton}
                    onClick={() => runIO(onProceedToApp({ email: email.trim(), password }))}
                    asChild={Option.none()}
                  >
                    Take me to the application
                  </Button>
                </div>
                <div className={styles.loginRow}>
                  <span>Need to switch accounts?</span>
                  <Button
                    variant="ghost"
                    className={styles.textButton}
                    onClick={() => runIO(onNavigateToLogin(okValue))}
                    data-testid="create-account-login"
                    asChild={Option.none()}
                  >
                    Log in
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    ),
    form: (
      <div className={styles.page} data-testid="page-createAccount" data-page-state="ready">
        <div className={styles.shell}>
          <aside className={styles.leftPanel}>
            <span className={styles.brandBadge}>Ouroboros AI</span>
            <h1 className={styles.headline}>Build the organization your team deserves.</h1>
            <p className={styles.subheadline}>
              Set up a new workspace, invite your team, and keep every audit-ready export in one place.
            </p>
            {leftPanelView[modePhase]}
          </aside>

          <section className={styles.rightPanel}>
            {rightPanelView[modePhase]}
          </section>
        </div>
      </div>
    ),
  } as const satisfies Record<CreateAccountSignupPhase, React.ReactElement>;

  return signupView[signupPhase];
};

export default CreateAccountPanel;
